import type { QualityTier } from '../../engine/Quality';
import { buildGraphics, readChoice, type Choice } from './graphics';
import { buildIdentity } from './identity';
import { buildFeedback } from './feedback';
import { readIdentity, type PlayerIdentity } from '../../game/Identity';
import {
  aboutSection,
  bestiarySection,
  feedbackSection,
  footerSection,
  heroSection,
  navBar,
  sceneLayer,
  storySection,
  trailerSection,
} from './sections';

/**
 * Must match `#landing`'s `transition: opacity` duration in `style.css` (0.55s).
 * Only a fallback deadline — `transitionend` is the real signal — so a small
 * drift costs nothing, but keep the two in step.
 */
const HIDE_FADE_MS = 550;

/**
 * Pre-launch marketing site shown over the (empty) game canvas. Paints
 * instantly — pure HTML/CSS, no WebGL, no Game construction. The game is built
 * only when the visitor presses PLAY: `onBegin` fires, a loading bar runs while
 * `Game.init()` works, then the page fades and play begins.
 *
 * A fixed CSS jungle scene sits behind every section as one continuous
 * backdrop. The top nav is transparent over the hero and gains a solid
 * background once scrolled.
 *
 * Public surface for main.ts:
 *   onBegin          — PLAY pressed (or auto-boot). main.ts builds + enters the game.
 *   selectedChoice   — the stored graphics choice ('auto' | tier).
 *   selectedIdentity — the player's name + voice (C7.1).
 *   markLoading      — reveal + animate the loading bar.
 *   markReady        — snap the bar to 100%.
 *   markError        — bar → error state, PLAY re-enabled to retry.
 *   hide             — fade the page out; awaitable, resolves once it is gone.
 *
 * Web-preview only; disposable per `.claude/rules/mobile.md`.
 */

const TRAILER_SRC = '/trailer.mp4';
const POSTER_SRC = '/og-image.png';
const POSTER_FALLBACK = '/og-image.png';

export class Landing {
  onBegin: () => void = () => {};
  /** Retained for API compatibility; the selector only persists now. */
  onChangeTier: (tier: QualityTier) => void = () => {};

  private readonly root: HTMLDivElement;
  private readonly playBtns: HTMLButtonElement[] = [];
  private readonly nav: HTMLElement;
  private readonly bar: HTMLDivElement;
  private readonly barFill: HTMLDivElement;
  private readonly barLabel: HTMLDivElement;
  private readonly graphicsChoice: () => Choice;
  private readonly identityValue: () => PlayerIdentity;
  private barTimer = 0;
  private engaged = false;

  constructor(parent: HTMLElement, _isTouch: boolean, detected: QualityTier) {
    this.root = el('div', 'landing') as HTMLDivElement;
    this.root.id = 'landing';

    const heroPlay = this.makePlayButton();
    const navPlay = this.makePlayButton('ls-nav-play');
    const graphics = buildGraphics(detected, (tier) => this.onChangeTier(tier));
    this.graphicsChoice = graphics.choice;
    const identity = buildIdentity();
    this.identityValue = identity.identity;
    const feedback = buildFeedback({ tier: detected, touch: _isTouch });

    // Loading bar — lives in the hero CTA, hidden until PLAY.
    this.bar = el('div', 'ls-bar') as HTMLDivElement;
    this.bar.hidden = true;
    this.barLabel = el('div', 'ls-bar-label', 'Entering the floodplain…') as HTMLDivElement;
    const track = el('div', 'ls-bar-track');
    this.barFill = el('div', 'ls-bar-fill') as HTMLDivElement;
    track.appendChild(this.barFill);
    this.bar.append(this.barLabel, track);

    this.nav = navBar(navPlay);

    const sections = document.createDocumentFragment();
    sections.append(
      heroSection(heroPlay, identity.el, graphics.el, this.bar),
      storySection(),
      trailerSection(TRAILER_SRC, POSTER_SRC, POSTER_FALLBACK),
      aboutSection(),
      bestiarySection(),
      feedbackSection(feedback.el),
      footerSection(),
    );

    this.root.append(sceneLayer(), this.nav, sections);
    parent.appendChild(this.root);
    document.getElementById('boot')?.remove();
    document.documentElement.classList.add('ls-scroll-lock');

    this.wireNavScroll();
    this.wireReveal();
  }

  selectedChoice(): Choice {
    return this.graphicsChoice ? this.graphicsChoice() : readChoice();
  }

  selectedIdentity(): PlayerIdentity {
    return this.identityValue ? this.identityValue() : readIdentity();
  }

  /** PLAY pressed: reveal the bar, disable PLAY, ease the fill toward ~96%. */
  markLoading(): void {
    if (this.engaged) return;
    this.engaged = true;
    for (const b of this.playBtns) b.disabled = true;
    this.bar.hidden = false;
    this.bar.classList.remove('error');
    this.barLabel.textContent = 'Entering the floodplain…';

    let pct = 0;
    const step = () => {
      pct += (92 - pct) * 0.035 + 0.4;
      this.barFill.style.width = `${Math.min(pct, 96)}%`;
      this.barTimer = window.setTimeout(step, 90);
    };
    step();
  }

  markReady(): void {
    window.clearTimeout(this.barTimer);
    this.barFill.style.width = '100%';
  }

  /**
   * Hold the bar at full while the first frame renders behind it.
   *
   * The first presented frame costs several seconds of GPU pipeline work that
   * no JS-side warmup absorbs (docs/ISSUES.md) — so rather than hide the
   * landing and freeze the player, we keep it up and spend the wait on the
   * mission briefing. Also the reason `begin()` is deferred until after it:
   * the raptor only hunts once the run has started, so nothing can reach the
   * player while this is on screen.
   */
  markWarming(text: string): void {
    window.clearTimeout(this.barTimer);
    this.barFill.style.width = '100%';
    this.barLabel.textContent = text;
  }

  markError(): void {
    window.clearTimeout(this.barTimer);
    this.engaged = false;
    this.bar.classList.add('error');
    this.barFill.style.width = '100%';
    this.barLabel.textContent = 'Failed to start — try a lower graphics setting.';
    for (const b of this.playBtns) {
      b.disabled = false;
      b.textContent = 'RETRY';
    }
  }

  /**
   * Dismiss the landing. Resolves when it has actually left the screen, not
   * when the fade *starts* — `.hidden` is an opacity transition, so for ~0.55 s
   * after this call the landing is still painted over the game.
   *
   * Callers must await it before anything the player is meant to see or hear:
   * `begin()` used to run on the next line and the opening monologue played
   * over a still-opaque landing (C7.2a). Resolves off `transitionend`, with a
   * timeout fallback so a browser that never fires it (reduced-motion, a
   * backgrounded tab) can never strand the boot.
   */
  hide(): Promise<void> {
    window.clearTimeout(this.barTimer);
    this.root.classList.add('hidden');
    document.documentElement.classList.remove('ls-scroll-lock');
    return new Promise<void>((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        this.root.removeEventListener('transitionend', onEnd);
        window.clearTimeout(timer);
        resolve();
      };
      const onEnd = (e: TransitionEvent) => {
        if (e.target === this.root && e.propertyName === 'opacity') finish();
      };
      this.root.addEventListener('transitionend', onEnd);
      const timer = window.setTimeout(finish, HIDE_FADE_MS + 120);
    });
  }

  private makePlayButton(extraClass?: string): HTMLButtonElement {
    const b = el(
      'button',
      `landing-begin${extraClass ? ' ' + extraClass : ''}`,
    ) as HTMLButtonElement;
    b.type = 'button';
    b.textContent = 'PLAY';
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!b.disabled) this.onBegin();
    });
    this.playBtns.push(b);
    return b;
  }

  /** Solid nav background once the hero has scrolled away. */
  private wireNavScroll(): void {
    const hero = this.root.querySelector('#ls-hero');
    if (!hero || typeof IntersectionObserver !== 'function') return;
    const obs = new IntersectionObserver(
      ([entry]) => this.nav.classList.toggle('scrolled', !entry.isIntersecting),
      { root: this.root, threshold: 0.12 },
    );
    obs.observe(hero);
  }

  /** Fade + rise `.ls-reveal` elements as they scroll into view (once). */
  private wireReveal(): void {
    const items = this.root.querySelectorAll<HTMLElement>('.ls-reveal');
    if (typeof IntersectionObserver !== 'function') {
      items.forEach((n) => n.classList.add('in'));
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            obs.unobserve(e.target);
          }
        }
      },
      { root: this.root, threshold: 0.2, rootMargin: '0px 0px -8% 0px' },
    );
    items.forEach((n) => obs.observe(n));
  }
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  n.className = className;
  if (text != null) n.textContent = text;
  return n;
}
